import { useEffect, useState } from "react";
import { Loader2, Plus, Wrench } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { TechnicianAPI, SkillAPI, UserAPI, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function TechniciansPage() {
    const { permissions } = useAuth();
    const canCreate = permissions.includes("technician:create");

    const [techs, setTechs] = useState([]);
    const [skills, setSkills] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const [t, s, u] = await Promise.all([
                TechnicianAPI.list({}),
                SkillAPI.list({ is_active: true }),
                UserAPI.list().catch(() => []),
            ]);
            setTechs(t.items || []);
            setSkills(s.items || []);
            setUsers(u || []);
        } catch (e) { toast.error(extractError(e)); }
        finally { setLoading(false); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    const usersById = users.reduce((acc, u) => ({ ...acc, [u.id]: u }), {});
    const skillsByCode = skills.reduce((acc, s) => ({ ...acc, [s.code]: s }), {});
    const assignedUserIds = new Set(techs.map((t) => t.user_id));
    const candidateUsers = users.filter((u) => !assignedUserIds.has(u.id) && u.is_active !== false);

    return (
        <div className="space-y-6 animate-fade-in" data-testid="technicians-page">
            <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Technicians</h1>
                    <p className="mt-1 text-sm text-zinc-500">Skill-tagged technicians. Owners can assign existing users into the technicians roster.</p>
                </div>
                {canCreate && <NewTechDialog users={candidateUsers} skills={skills} onCreated={load} />}
            </header>

            {loading ? (
                <Card className="grid place-items-center p-12 text-sm text-zinc-500"><Loader2 className="h-5 w-5 animate-spin" /></Card>
            ) : techs.length === 0 ? (
                <Card className="grid place-items-center p-12 text-sm text-zinc-500">No technicians yet.</Card>
            ) : (
                <Card className="overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="border-b bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500 dark:bg-zinc-900">
                            <tr>
                                <th className="px-4 py-3">Employee</th>
                                <th className="px-4 py-3">User</th>
                                <th className="px-4 py-3">Skills</th>
                                <th className="px-4 py-3">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {techs.map((t) => {
                                const u = t.user || usersById[t.user_id];
                                return (
                                    <tr key={t.id} className="border-b last:border-b-0" data-testid={`tech-row-${t.id}`}>
                                        <td className="px-4 py-3 font-mono text-xs">{t.employee_code}</td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{u?.name || "—"}</div>
                                            <div className="text-xs text-zinc-500 font-mono">{u?.email || ""}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {(t.skills || []).map((code) => (
                                                    <span key={code} className="inline-flex items-center rounded bg-brand-redSoft px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider text-brand-redDeep dark:bg-brand-red/15 dark:text-brand-red/80">
                                                        {skillsByCode[code]?.name || code}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            {t.is_active ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />active</span> : <span className="text-xs text-zinc-400">inactive</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Card>
            )}
        </div>
    );
}

function NewTechDialog({ users, skills, onCreated }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ user_id: "", skills: [] });

    function toggleSkill(code) {
        setForm((f) => ({
            ...f,
            skills: f.skills.includes(code) ? f.skills.filter((c) => c !== code) : [...f.skills, code],
        }));
    }

    async function submit() {
        setBusy(true);
        try {
            await TechnicianAPI.create({ user_id: form.user_id, skills: form.skills, branch_ids: [] });
            toast.success("Technician added");
            setOpen(false);
            setForm({ user_id: "", skills: [] });
            onCreated && onCreated();
        } catch (e) { toast.error(extractError(e)); }
        finally { setBusy(false); }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="bg-brand-red text-white hover:bg-brand-redDeep" data-testid="tech-new-btn"><Plus className="mr-1 h-4 w-4" /> Add technician</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Add technician</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">User</Label>
                        <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="tech-user">
                            <option value="">Pick a user…</option>
                            {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.email} ({u.role})</option>)}
                        </select>
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Skills ({form.skills.length})</Label>
                        <div className="flex flex-wrap gap-1.5 rounded-md border border-input p-2">
                            {skills.map((s) => (
                                <button key={s.code} type="button" onClick={() => toggleSkill(s.code)} data-testid={`tech-skill-${s.code}`}
                                    className={`rounded-md border px-2 py-1 text-xs ${form.skills.includes(s.code) ? "border-brand-red bg-brand-redSoft text-brand-redDeep dark:bg-brand-red/15 dark:text-brand-red" : "border-zinc-200 dark:border-zinc-800"}`}>
                                    {s.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || !form.user_id} className="bg-brand-red text-white hover:bg-brand-redDeep" data-testid="tech-save">
                        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} <Wrench className="mr-1 h-4 w-4" /> Add
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
